/**
 * The "tick what it covers" picker, shared by every composer.
 *
 * Written for the booklet composer first, where a teacher picks topics instead
 * of describing them, and lifted here unchanged in behaviour so presentations,
 * worksheets and quizzes offer the same thing: the same rows, the same ids on
 * the wire, the same .bk-topic-* classes so there is one set of styles.
 *
 * What it knows: how to fetch a topic list for a board/subject/level, how to
 * draw it, and which ids are ticked. What it deliberately does not know: what
 * the surface around it does with that selection. The booklet composer swaps
 * its brief box for the list; the other three fill in their topic line from it
 * and are otherwise unchanged. Both come out of the onChange callback.
 *
 * 204 from the endpoint is the ordinary answer, not a failure: we hold
 * specifications for hundreds of combinations and authored topic lists for a
 * couple of dozen, so most selections keep the free-text box they always had.
 * Any error does the same thing — a composer that cannot offer a tick list
 * must still be able to make the resource.
 */
(function (root) {
    'use strict';

    const TICK_SVG = '<svg viewBox="0 0 10.1668 10.1668" aria-hidden="true"><path d="M1 5.52L3.92 9.17L9.17 1"/></svg>';
    const CHEV_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';

    function esc(str) {
        return String(str ?? '')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    /**
     * One topic row, plus its points where it has any.
     *
     * A topic ticks as a whole and that is the common path — a teacher wants
     * "Energy stores and systems", not three of its four objectives. The
     * points sit behind a collapsed disclosure for when they do want to narrow
     * it, which is also what makes a source written with a `**bold**` tier as
     * precise as one written without: before this, the first kind could only
     * be ticked in broad strokes while the second exposed every objective.
     */
    function topicRow(t) {
        const box = '<label class="bk-topic">'
            + '<input type="checkbox" value="' + esc(t.id) + '">'
            + '<span class="bk-topic-check" aria-hidden="true">' + TICK_SVG + '</span>'
            + '<span class="bk-topic-text"><span class="bk-topic-label">' + esc(t.label) + '</span>'
            + (t.note ? '<span class="bk-topic-note">' + esc(t.note) + '</span>' : '')
            + '</span></label>';

        if (!t.points || !t.points.length) return '<div class="bk-topic-group">' + box + '</div>';

        // The disclosure is a sibling of the label, not inside it: a button
        // nested in a <label> is activated by the label's own click handling,
        // so expanding would toggle the tick as well.
        return '<div class="bk-topic-group">'
            + '<div class="bk-topic-row">' + box
            + '<button type="button" class="bk-topic-toggle" aria-expanded="false"'
            + ' data-topic="' + esc(t.id) + '">'
            + '<span class="bk-topic-toggle-count">' + t.points.length + '</span>'
            + CHEV_SVG
            + '<span class="sr-only">Show the ' + t.points.length + ' points in ' + esc(t.label) + '</span>'
            + '</button></div>'
            + '<div class="bk-topic-points" hidden>'
            + t.points.map((pt) => '<label class="bk-topic bk-topic-point">'
                + '<input type="checkbox" value="' + esc(pt.id) + '">'
                + '<span class="bk-topic-check" aria-hidden="true">' + TICK_SVG + '</span>'
                + '<span class="bk-topic-text"><span class="bk-topic-label">' + esc(pt.label) + '</span></span>'
                + '</label>').join('')
            + '</div></div>';
    }

    /**
     * Repaint one topic's parent box from its points.
     *
     * Three states, and the indeterminate one matters: a parent shown as
     * unticked while two of its points are ticked reads as "this topic is not
     * in the resource", which is the opposite of what was asked for.
     */
    function syncParent(group) {
        const parent = group.querySelector('.bk-topic > input');
        const points = [...group.querySelectorAll('.bk-topic-point input')];
        if (!parent || !points.length) return;
        const on = points.filter((b) => b.checked).length;
        parent.checked = on === points.length;
        parent.indeterminate = on > 0 && on < points.length;
        group.classList.toggle('is-partial', parent.indeterminate);
    }

    /**
     * @param {object} o
     * @param {Element} o.group     wrapper shown only when a list exists
     * @param {Element} o.list      where the rows are drawn
     * @param {Element} [o.countEl]
     * @param {Element} [o.clearBtn]
     * @param {function} o.apiFetch the app's credentialed fetch
     * @param {function} [o.onChange] called with the picker after every load
     *                                and every change, including empty loads
     */
    function create(o) {
        const { group, list, countEl, clearBtn, apiFetch } = o;
        if (!group || !list || !apiFetch) return null;

        let catalogue = null;
        const ticked = new Set();
        // The selection can change while a fetch is in flight — a teacher
        // correcting the board right after the subject — and a late reply
        // would otherwise paint the previous subject's topics over the new one.
        let seq = 0;

        const api = {
            get catalogue() { return catalogue; },
            /** Ticked ids, in the order they were ticked. */
            ids() { return [...ticked]; },
            /**
             * Topics touched, not ids held: three points inside one topic are
             * three ids but one topic's worth of coverage, and every caller
             * that counts is counting sections or lines, not ids.
             */
            count() {
                return new Set([...ticked].map((id) => id.replace(/\.p\d+$/, ''))).size;
            },
            total() {
                return catalogue ? catalogue.domains.reduce((a, d) => a + d.topics.length, 0) : 0;
            },
            /**
             * The selection as one readable line — what a composer with a
             * single topic field puts in it.
             *
             * A partly-opened topic is named by the points chosen, not by the
             * topic: "Energy stores and systems" would claim four objectives
             * when the teacher asked for one. Mirrors summariseTopics() in
             * utils/specTopics.js, which produces the stored version.
             */
            summary(max = 500) {
                if (!catalogue) return '';
                const parts = [];
                for (const d of catalogue.domains) {
                    for (const t of d.topics) {
                        if (ticked.has(t.id)) { parts.push(t.label); continue; }
                        const points = (t.points || []).filter((p) => ticked.has(p.id));
                        if (points.length) parts.push(points.map((p) => p.label).join('; '));
                    }
                }
                const s = parts.join('; ');
                return s.length > max ? s.slice(0, max - 1).trimEnd() + '…' : s;
            },
            clear() {
                ticked.clear();
                list.querySelectorAll('input[type="checkbox"]').forEach((b) => {
                    b.checked = false;
                    b.indeterminate = false;
                });
                list.querySelectorAll('.bk-topic-group').forEach((g) => g.classList.remove('is-partial'));
                paint();
            },
            focusFirst() { list.querySelector('input')?.focus(); },
            /**
             * Ticks these ids, as saved earlier, on the list already loaded.
             * Unknown ids (a list re-authored since) are dropped quietly.
             */
            select(ids) {
                if (!catalogue) return;
                ticked.clear();
                const known = new Set([...list.querySelectorAll('input[type="checkbox"]')].map((b) => b.value));
                for (const id of ids || []) if (known.has(String(id))) ticked.add(String(id));
                list.querySelectorAll('input[type="checkbox"]').forEach((b) => {
                    const topic = b.value.replace(/\.p\d+$/, '');
                    b.checked = ticked.has(b.value) || (b.value !== topic && ticked.has(topic));
                    b.indeterminate = false;
                });
                list.querySelectorAll('.bk-topic-group').forEach(syncParent);
                paint();
            },
            /** Load the list for this selection, or hide the picker if none. */
            async load(board, subject, level, tier) {
                const mine = ++seq;
                catalogue = null;
                ticked.clear();
                if (!board || !subject || !level) { render(); return; }
                try {
                    const res = await apiFetch('/api/spec-coverage/topics?board=' + encodeURIComponent(board)
                        + '&subject=' + encodeURIComponent(subject)
                        + '&level=' + encodeURIComponent(level)
                        + (tier ? '&tier=' + encodeURIComponent(tier) : ''));
                    if (mine !== seq) return;
                    catalogue = res.status === 204 ? null : await res.json();
                } catch {
                    catalogue = null;
                }
                if (mine !== seq) return;
                render();
            },
        };

        function paint() {
            if (countEl) {
                const n = api.count();
                countEl.textContent = n ? n + ' of ' + api.total() + ' selected' : api.total() + ' topics';
            }
            if (clearBtn) clearBtn.disabled = !ticked.size;
            o.onChange?.(api);
        }

        function render() {
            if (!catalogue) {
                group.hidden = true;
                list.innerHTML = '';
                o.onChange?.(api);
                return;
            }
            list.innerHTML = catalogue.domains.map((d) => '<div class="bk-topic-domain">'
                + '<div class="bk-topic-domain-name">' + esc(d.name) + '</div>'
                + d.topics.map(topicRow).join('')
                + '</div>').join('');
            group.hidden = false;
            paint();
        }

        list.addEventListener('change', (e) => {
            const box = e.target.closest('input[type="checkbox"]');
            if (!box) return;
            const groupEl = box.closest('.bk-topic-group');
            const isPoint = !!box.closest('.bk-topic-point');

            if (isPoint) {
                const parent = groupEl.querySelector('.bk-topic > input');
                const points = [...groupEl.querySelectorAll('.bk-topic-point input')];

                // A whole topic is held as one id with nothing beneath it, so
                // narrowing one has to expand it into its points first —
                // otherwise unticking a single objective would drop the entire
                // topic.
                if (ticked.delete(parent.value)) {
                    points.forEach((b) => { if (b !== box && b.checked) ticked.add(b.value); });
                }
                if (box.checked) ticked.add(box.value); else ticked.delete(box.value);

                if (points.every((b) => b.checked)) {
                    // All of them is the topic. Collapsed back to the topic id
                    // so the resource still covers the whole of it if the
                    // source later gains an objective.
                    points.forEach((b) => ticked.delete(b.value));
                    ticked.add(parent.value);
                }
                syncParent(groupEl);
            } else {
                // The topic id carries the selection on its own, but the points
                // beneath it still have to LOOK selected — a ticked topic
                // showing empty boxes inside reads as though nothing in it was
                // chosen.
                groupEl?.querySelectorAll('.bk-topic-point input').forEach((b) => {
                    b.checked = box.checked;
                    ticked.delete(b.value);
                });
                if (box.checked) ticked.add(box.value); else ticked.delete(box.value);
                if (groupEl) {
                    groupEl.classList.remove('is-partial');
                    box.indeterminate = false;
                }
            }
            paint();
        });

        list.addEventListener('click', (e) => {
            const btn = e.target.closest('.bk-topic-toggle');
            if (!btn) return;
            const points = btn.closest('.bk-topic-group')?.querySelector('.bk-topic-points');
            if (!points) return;
            const open = btn.getAttribute('aria-expanded') === 'true';
            btn.setAttribute('aria-expanded', open ? 'false' : 'true');
            points.hidden = open;
        });

        clearBtn?.addEventListener('click', () => api.clear());

        return api;
    }

    root.TopicPicker = { create };
}(window));
